import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import { ModalAttachStyled } from './styled';
import { withI18n } from '../../../hocs';
import { MODAL_TYPE, FILE_INPUT_SPECS, MSG_CONTENT_TYPES, MSG_SEND_TYPE } from '../../../models';
import * as api from '../../../api';
import { sendMessage } from '../../../sockets/emit';
import { useSocketContext } from '../../../sockets/context';
import { SendIcon, CloudUploadIcon } from '../../atoms';
import useMessageRetry from '../../../hooks/useMessageRetry';
import { addMessageToDB } from '../../../db/dbUtils';

function CancelButton({ onClick, label }) {
  return (
    <button onClick={onClick} className="modal-button-cancel ts" type="button">
      {label}
    </button>
  );
}
CancelButton.propTypes = { onClick: PropTypes.func, label: PropTypes.string };

function SendButton({ onClick, label, disabled, isFetching }) {
  return (
    <button disabled={disabled} type="button" className="upload-file-button ts" onClick={onClick}>
      <p>{label}</p>
      {isFetching ? <ClipLoader size={16} loading={isFetching} /> : <SendIcon className="upload-icon ts" />}
    </button>
  );
}
SendButton.propTypes = { onClick: PropTypes.func, label: PropTypes.string, disabled: PropTypes.bool, isFetching: PropTypes.bool };

const getModalSpecsFromAction = ({ action, i18n }) => {
  const modalSpecs = {};
  switch (action) {
    case MODAL_TYPE.ATTACH.IMAGE:
      modalSpecs.title = i18n.attachImageModal.title;
      modalSpecs.send = i18n.attachImageModal.send;
      modalSpecs.uploadButton = i18n.attachImageModal.button;
      modalSpecs.limitations = i18n.attachImageModal.limitations;
      modalSpecs.supportedFormat = i18n.attachImageModal.supportedFormat;
      modalSpecs.cancel = i18n.buttons.cancel;
      modalSpecs.fileAcceptFormats = FILE_INPUT_SPECS.ACCEPT.IMAGE;
      modalSpecs.maxFileSize = FILE_INPUT_SPECS.MAX_SIZE.IMAGE;
      modalSpecs.contentType = MSG_CONTENT_TYPES.IMAGE;
      break;
    case MODAL_TYPE.ATTACH.VIDEO:
      modalSpecs.title = i18n.attachVideoModal.title;
      modalSpecs.send = i18n.attachVideoModal.send;
      modalSpecs.uploadButton = i18n.attachVideoModal.button;
      modalSpecs.limitations = i18n.attachVideoModal.limitations;
      modalSpecs.supportedFormat = i18n.attachVideoModal.supportedFormat;
      modalSpecs.cancel = i18n.buttons.cancel;
      modalSpecs.fileAcceptFormats = FILE_INPUT_SPECS.ACCEPT.VIDEO;
      modalSpecs.maxFileSize = FILE_INPUT_SPECS.MAX_SIZE.VIDEO;
      modalSpecs.contentType = MSG_CONTENT_TYPES.VIDEO;
      break;
    case MODAL_TYPE.ATTACH.DOCUMENT:
      modalSpecs.title = i18n.attachDocumentModal.title;
      modalSpecs.send = i18n.attachDocumentModal.send;
      modalSpecs.uploadButton = i18n.attachDocumentModal.button;
      modalSpecs.limitations = i18n.attachDocumentModal.limitations;
      modalSpecs.supportedFormat = i18n.attachDocumentModal.supportedFormat;
      modalSpecs.cancel = i18n.buttons.cancel;
      modalSpecs.fileAcceptFormats = FILE_INPUT_SPECS.ACCEPT.DOCUMENT;
      modalSpecs.maxFileSize = FILE_INPUT_SPECS.MAX_SIZE.DOCUMENT;
      modalSpecs.contentType = MSG_CONTENT_TYPES.DOCUMENT;
      break;
    case MODAL_TYPE.ATTACH.AUDIO:
      modalSpecs.title = i18n.attachAudioModal.title;
      modalSpecs.send = i18n.attachAudioModal.send;
      modalSpecs.uploadButton = i18n.attachAudioModal.button;
      modalSpecs.limitations = i18n.attachAudioModal.limitations;
      modalSpecs.supportedFormat = i18n.attachAudioModal.supportedFormat;
      modalSpecs.cancel = i18n.buttons.cancel;
      modalSpecs.fileAcceptFormats = FILE_INPUT_SPECS.ACCEPT.AUDIO;
      modalSpecs.maxFileSize = FILE_INPUT_SPECS.MAX_SIZE.AUDIO;
      modalSpecs.contentType = MSG_CONTENT_TYPES.AUDIO;
      break;
    default:
  }
  return modalSpecs;
};

function ModalAttach({ show, i18n, onModalCancel, action }) {
  const [isFetching, setIsFetching] = useState(false);
  const [isTooBigFile, setIsTooBigFile] = useState(false);
  const [isValidFile, setIsValidFile] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState(null);
  const [fileType, setFileType] = useState(null);

  const { conversations, selectedChatId, setIsMessageSent, setRetryTimers } = useSocketContext();
  const { title, send, cancel, uploadButton, limitations, supportedFormat, fileAcceptFormats, maxFileSize, contentType } =
    getModalSpecsFromAction({ action, i18n });

  // Hook de retry: devuelve función que envía y maneja reintentos
  const sendWithRetry = useMessageRetry(sendMessage, setRetryTimers);

  useEffect(() => {
    setFileType(contentType);
  }, [contentType]);

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const { name, size, type } = file;
    const valid = Boolean(name && size > 0);
    setIsValidFile(valid);
    if (size > maxFileSize) {
      setIsTooBigFile(true);
      setSelectedFile(null);
      setSelectedFileName(null);
      setFileType(null);
    } else {
      setIsTooBigFile(false);
      setSelectedFile(file);
      setSelectedFileName(name);
      setFileType(type);
    }
  };

  const closeAndReset = () => {
    setIsValidFile(true);
    setIsTooBigFile(false);
    setSelectedFile(null);
    setSelectedFileName(null);
    setFileType(null);
    onModalCancel();
  };

  const generateGuid = () => (self.crypto?.randomUUID() || null);

  const sendButtonHandler = async () => {
    if (!selectedFile) return;
    setIsFetching(true);
    const channel = conversations.find((c) => c.conversationSessionId === selectedChatId)?.channel;
    try {
      const normalizedType = fileType.startsWith('image/')
  ? 'image'
  : fileType.startsWith('video/')
  ? 'video'
  : fileType.startsWith('audio/')
  ? 'audio'
  : 'document';
      const response = await api.postMultimedia(channel, selectedFile, normalizedType);
      if (response.status === 200) {
        const { id, multimediaType } = response.data.data;
        // construir mensaje para retry
        const guid = generateGuid();
        const message = {
          conversationSessionId: selectedChatId,
          content: { id, caption: selectedFileName },
          contentType: multimediaType,
          agentMessageId: guid,
          sendType: MSG_SEND_TYPE.OPERATOR,
          status: 'queued',
          retries: 0,
          timestamp: Date.now(),
        };
        await addMessageToDB(message);
        sendMessage(message);
        sendWithRetry(message);
        setIsMessageSent(false);
      }
    } catch (error) {
      if (error.response?.status === 400) {
        setIsValidFile(false);
      } else if (error.response?.status === 500) {
        toast(i18n.toast.error500Attachfile, { type: toast.TYPE.ERROR });
      }
    } finally {
      setIsFetching(false);
      closeAndReset();
    }
  };

  const fileError = isTooBigFile || !isValidFile;
  const disabled = fileError || !selectedFileName;
  const acceptAttr = Array.isArray(fileAcceptFormats) ? fileAcceptFormats.join(',') : fileAcceptFormats;

  return (
    <ModalAttachStyled show={show}>
      <div className="modal-content">
        <h2 className="modal-title">{title}</h2>
        <div className="modal-body">
          <form>
            <label className="input-file-label" htmlFor="file">
              <CloudUploadIcon className="upload-icon" />
              <span>{uploadButton}</span>
            </label>
            <input
              className="input-file"
              accept={acceptAttr}
              id="file"
              type="file"
              onChange={onFileChange}
              onClick={(e) => (e.target.value = null)}
            />
          </form>
          {fileError && (
            <div className="file-error">
              {isTooBigFile && <p className="file-error-text">{i18n.errors.fileTooBig}</p>}
              {!isValidFile && <p className="file-error-text">{i18n.errors.fileNoValid}</p>}
            </div>
          )}
          {selectedFileName && (
            <div className="file-name">
              <p className="file-name-text">{selectedFileName}</p>
            </div>
          )}
          <div className="modal-text-holder">
            <p className="modal-text">{limitations}</p>
            <p className="modal-text">{supportedFormat}</p>
          </div>
          <div className="modal-buttons-row">
            <CancelButton onClick={closeAndReset} label={cancel} />
            <SendButton isFetching={isFetching} label={send} disabled={disabled || isFetching} onClick={sendButtonHandler} />
          </div>
        </div>
      </div>
    </ModalAttachStyled>
  );
}

ModalAttach.propTypes = {
  show: PropTypes.bool,
  i18n: PropTypes.object,
  onModalCancel: PropTypes.func,
  action: PropTypes.oneOf([
    MODAL_TYPE.ATTACH.IMAGE,
    MODAL_TYPE.ATTACH.VIDEO,
    MODAL_TYPE.ATTACH.DOCUMENT,
    MODAL_TYPE.ATTACH.AUDIO,
  ]),
};

export default withI18n(ModalAttach);
